import React from "react";
import styled from "styled-components";
import { Experience, Education } from "../types";
import { Container, Section, SectionTitle, Card } from "../styles/GlobalStyles";
import { useLanguage } from "../contexts/LanguageContext";

const ExperienceContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const Timeline = styled.div`
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0.3;
  }
`;

const TimelineItem = styled(Card)`
  margin-bottom: 2rem;
  margin-left: 60px;
  position: relative;
  padding: 2rem;

  &::before {
    content: "";
    position: absolute;
    left: -58px;
    top: 2rem;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    border: 4px solid white;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
  }
`;

const ItemHeader = styled.div`
  margin-bottom: 1rem;
`;

const Company = styled.h3`
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 0.3rem;
  font-weight: 600;
`;

const Position = styled.h4`
  font-size: 1.1rem;
  color: #667eea;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const DateRange = styled.span`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Description = styled.ul`
  margin: 1rem 0;

  li {
    color: #666;
    line-height: 1.6;
    margin-bottom: 0.5rem;
    position: relative;
    padding-left: 1rem;

    &::before {
      content: "•";
      color: #667eea;
      position: absolute;
      left: 0;
      font-weight: bold;
    }
  }
`;

const TechnologiesUsed = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const TechTag = styled.span`
  background: #f1f3f5;
  color: #667eea;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const SectionHeader = styled.div`
  margin-bottom: 2rem;

  h3 {
    font-size: 1.8rem;
    color: #2c3e50;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1rem;
  }
`;

interface ExperienceProps {
  experiences: Experience[];
  education: Education[];
}

const ExperienceSection: React.FC<ExperienceProps> = ({
  experiences,
  education,
}) => {
  const { t, language } = useLanguage();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = language === "fr" ? "fr-FR" : "en-US";
    return date.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
    });
  };

  return (
    <Section id="experience">
      <Container>
        <SectionTitle>{t("experience.title")}</SectionTitle>

        <ExperienceContent>
          {/* Experience professionnelle */}
          <div>
            <SectionHeader>
              <h3>{t("experience.work")}</h3>
              <p>
                {t("experience.workDescription") ||
                  "Mon parcours professionnel et les projets sur lesquels j'ai travaillé"}
              </p>
            </SectionHeader>

            <Timeline>
              {experiences.map((exp) => (
                <TimelineItem key={exp.id}>
                  <ItemHeader>
                    <Company>{exp.company}</Company>
                    <Position>{exp.position}</Position>
                    <DateRange>
                      {formatDate(exp.startDate)} -{" "}
                      {exp.endDate
                        ? formatDate(exp.endDate)
                        : t("experience.present")}
                    </DateRange>
                  </ItemHeader>

                  <Description>
                    {exp.description.map((desc, index) => (
                      <li key={index}>{desc}</li>
                    ))}
                  </Description>

                  <TechnologiesUsed>
                    {exp.technologies.map((tech, index) => (
                      <TechTag key={index}>{tech}</TechTag>
                    ))}
                  </TechnologiesUsed>
                </TimelineItem>
              ))}
            </Timeline>
          </div>

          {/* Formation */}
          <div>
            <SectionHeader>
              <h3>{t("experience.education")}</h3>
              <p>
                {t("experience.educationDescription") ||
                  "Mon parcours académique et mes certifications"}
              </p>
            </SectionHeader>

            <Timeline>
              {education.map((edu) => (
                <TimelineItem key={edu.id}>
                  <ItemHeader>
                    <Company>{edu.school}</Company>
                    <Position>{edu.degree}</Position>
                    <DateRange>
                      {formatDate(edu.startDate)} -{" "}
                      {edu.endDate
                        ? formatDate(edu.endDate)
                        : language === "fr"
                        ? "En cours"
                        : "Ongoing"}
                    </DateRange>
                  </ItemHeader>

                  {edu.description && (
                    <Description>
                      <li>{edu.description}</li>
                    </Description>
                  )}
                </TimelineItem>
              ))}
            </Timeline>
          </div>
        </ExperienceContent>
      </Container>
    </Section>
  );
};

export default ExperienceSection;
